// src/utils/overpass.ts

/**
 * Utilidades para interactuar con la API de Overpass (OpenStreetMap)
 * Endpoint público: https://overpass-api.de/api/interpreter
 */

// ----------------------------------------------------------------------
// Tipos de datos de Overpass
// ----------------------------------------------------------------------

export interface OverpassElementBase {
  type: 'node' | 'way' | 'relation';
  id: number;
  tags?: Record<string, string>;
}

export interface OverpassNode extends OverpassElementBase {
  type: 'node';
  lat: number;
  lon: number;
}

export interface OverpassWay extends OverpassElementBase {
  type: 'way';
  nodes: number[]; // IDs de nodos que la componen
}

export interface OverpassRelationMember {
  type: 'node' | 'way' | 'relation';
  ref: number;
  role: string; // 'outer', 'inner', etc.
}

export interface OverpassRelation extends OverpassElementBase {
  type: 'relation';
  members: OverpassRelationMember[];
}

export type OverpassElement = OverpassNode | OverpassWay | OverpassRelation;

export interface OverpassResponse {
  version: number;
  generator: string;
  osm3s?: {
    timestamp_osm_base?: string;
    copyright?: string;
  };
  elements: OverpassElement[];
}

// ----------------------------------------------------------------------
// Configuración
// ----------------------------------------------------------------------

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter', // endpoint alternativo con límites más generosos
];

let endpointIndex = 0;
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 2000; // ms entre solicitudes (para no saturar el servidor público)

// ----------------------------------------------------------------------
// Función principal de consulta
// ----------------------------------------------------------------------

/**
 * Realiza una consulta a Overpass API mediante POST.
 * @param query - Consulta en lenguaje Overpass QL
 * @param timeoutMs - Tiempo máximo de espera (ms)
 * @returns Respuesta JSON parseada
 */
export async function fetchOverpassData(
  query: string,
  timeoutMs = 30000
): Promise<OverpassResponse> {
  // Control de rate limiting básico
  const now = Date.now();
  const waitTime = Math.max(0, lastRequestTime + MIN_REQUEST_INTERVAL - now);
  if (waitTime > 0) {
    await new Promise((resolve) => setTimeout(resolve, waitTime));
  }
  lastRequestTime = Date.now();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(OVERPASS_ENDPOINTS[endpointIndex], {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: 'data=' + encodeURIComponent(query),
      signal: controller.signal,
    });

    if (!response.ok) {
      // Si el primer endpoint falla, intentar con el alternativo
      if (endpointIndex === 0) {
        endpointIndex = 1;
        return fetchOverpassData(query, timeoutMs);
      }
      throw new Error(`Overpass API error: ${response.status} ${response.statusText}`);
    }

    const data: OverpassResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('La solicitud a Overpass excedió el tiempo límite');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ----------------------------------------------------------------------
// Consultas predefinidas
// ----------------------------------------------------------------------

/**
 * Construye una consulta para obtener edificios y calles alrededor de un punto.
 * @param lat - Latitud central
 * @param lng - Longitud central
 * @param radiusMeters - Radio de búsqueda en metros
 * @param includeRoads - Si se deben incluir calles
 * @param roadFilter - Expresión regular para tipos de carretera (highway)
 * @returns Consulta Overpass QL
 */
export function buildCityQuery(
  lat: number,
  lng: number,
  radiusMeters = 500,
  includeRoads = true,
  roadFilter = '^(primary|secondary|tertiary|residential|service)$'
): string {
  const aroundClause = `around:${radiusMeters},${lat},${lng}`;
  const buildingWays = `way["building"](${aroundClause});`;
  const buildingRelations = `relation["building"](${aroundClause});`;
  const roadWays = includeRoads
    ? `way["highway"~"${roadFilter}"](${aroundClause});`
    : '';

  return `
    [out:json][timeout:25];
    (
      ${buildingWays}
      ${buildingRelations}
      ${roadWays}
    );
    out tags body;
    >;
    out skel qt;
  `;
}

/**
 * Obtiene datos de ciudad (edificios y calles) desde Overpass.
 * @param lat - Latitud central
 * @param lng - Longitud central
 * @param radiusMeters - Radio en metros
 * @returns Respuesta cruda de Overpass
 */
export async function getCityData(
  lat: number,
  lng: number,
  radiusMeters = 500
): Promise<OverpassResponse> {
  const query = buildCityQuery(lat, lng, radiusMeters);
  return fetchOverpassData(query);
}